import { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useQueryClient } from '@tanstack/react-query';
import {
  listGraphs,
  rdfCommitImport,
  rdfImportNamespaces,
  rdfPreviewImport,
  rdfProfileSpreadsheet,
  type GraphInfo,
  type LoadMode,
  type RdfObjectMap,
  type RdfPreviewResponse,
  type RdfReconciliationReport,
  type RdfSkosAttachment,
  type RdfSpreadsheetTemplate,
  type RdfUserChoice,
} from '@/lib/api';
import { ConfirmGraphReplaceModal } from '@/components/ConfirmGraphReplaceModal';
import { invalidateAfterDataWrite } from '@/lib/invalidate';
import { useMaxLoadBytes } from '@/lib/usePages';
import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/Button';
import { messageClass } from '@/components/ui/Message';

// ---------------------------------------------------------------------------
// RDF spreadsheet import wizard (Phase 1: primary entity + properties +
// relationships + reconcile + target graph + append/replace). Mirrors the QROS
// SpreadsheetImportPanel's stepper/upload/preview patterns; the mapping it
// authors is the desktop `SpreadsheetTemplate`.
// ---------------------------------------------------------------------------

type Step = 'load' | 'map' | 'preview' | 'done';

type Datatype = 'string' | 'number' | 'date' | 'boolean';
type Role = 'skip' | 'property' | 'relationship';
type Resolution = 'create' | 'match_label';

type SkosMode = 'none' | 'top' | 'child';

interface ColumnMapping {
  role: Role;
  // property
  predicate: string;
  datatype: Datatype;
  /** Which entity this property attaches to: '' = primary, else the column
   *  header of a relationship (its target entity). */
  attachTo: string;
  // relationship
  targetClass: string;
  linkPredicate: string;
  resolution: Resolution;
  /** Also emit the key value as a literal property on the target entity. */
  alsoAsProp: boolean;
  alsoAsPropPredicate: string;
  /** SKOS placement for a newly-minted concept target. */
  skosMode: SkosMode;
  skosScheme: string; // for 'top'
  skosParent: string; // for 'child'
}

const DEFAULT_GRAPH = 'urn:tres:data';

function defaultMapping(): ColumnMapping {
  return {
    role: 'skip',
    predicate: '',
    datatype: 'string',
    attachTo: '',
    targetClass: '',
    linkPredicate: '',
    resolution: 'match_label',
    alsoAsProp: false,
    alsoAsPropPredicate: '',
    skosMode: 'none',
    skosScheme: '',
    skosParent: '',
  };
}

function objectMapFor(dt: Datatype): RdfObjectMap {
  switch (dt) {
    case 'number':
      return 'TypedNumber';
    case 'date':
      return { TypedDate: { format: null } };
    case 'boolean':
      return 'TypedBoolean';
    default:
      return { Literal: { datatype: null, lang: null } };
  }
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function TabButton({
  active,
  done,
  children,
}: {
  active: boolean;
  done: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'border-b-2 px-3 py-1.5 text-sm font-medium',
        active ? 'border-tres-action text-tres-action' : 'border-transparent',
        !active && done ? 'text-tres-text-primary' : '',
        !active && !done ? 'text-tres-text-secondary' : '',
      )}
    >
      {children}
    </div>
  );
}

export function RdfSpreadsheetImportPanel() {
  const auth = useAuth();
  const token = auth.user?.id_token ?? '';
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>('load');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const maxLoadBytes = useMaxLoadBytes().data ?? null;

  // load
  const [file, setFile] = useState<File | null>(null);
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [selectedSheet, setSelectedSheet] = useState<string | null>(null);
  const [columns, setColumns] = useState<string[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [baseUri, setBaseUri] = useState('');
  const [namespaceOptions, setNamespaceOptions] = useState<string[]>([]);

  // map
  const [keyColumn, setKeyColumn] = useState<string>('');
  const [keyClass, setKeyClass] = useState('');
  const [keyResolution, setKeyResolution] = useState<Resolution>('create');
  const [mappings, setMappings] = useState<Record<string, ColumnMapping>>({});

  // preview / commit
  const [graphs, setGraphs] = useState<GraphInfo[]>([]);
  const [targetGraph, setTargetGraph] = useState(DEFAULT_GRAPH);
  const [newGraph, setNewGraph] = useState('');
  const [writeMode, setWriteMode] = useState<LoadMode>('append');
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [preview, setPreview] = useState<RdfPreviewResponse | null>(null);
  const [choices, setChoices] = useState<Record<string, RdfUserChoice>>({});
  const [commitResult, setCommitResult] = useState<{
    rows_imported: number;
    rows_updated: number;
    rows_skipped: number;
    triples_written: number;
    graph: string;
    errors: string[];
  } | null>(null);

  const mapping = (col: string): ColumnMapping => mappings[col] ?? defaultMapping();
  const patchMapping = (col: string, patch: Partial<ColumnMapping>) =>
    setMappings((m) => ({ ...m, [col]: { ...mapping(col), ...patch } }));

  async function analyze(f: File, sheet?: string) {
    setBusy(true);
    setError(null);
    const [res, ns] = await Promise.all([
      rdfProfileSpreadsheet(f, token, sheet),
      rdfImportNamespaces(token),
    ]);
    setBusy(false);
    if (!res.ok) {
      setError(res.message);
      return;
    }
    setColumns(res.data.columns);
    setRows(res.data.rows);
    setSheetNames(res.data.sheet_names);
    setSelectedSheet(res.data.selected_sheet);
    setFile(f);
    if (ns.ok) {
      setNamespaceOptions(ns.data.namespaces);
      // Default the base IRI to the data's own namespace when we haven't set one.
      setBaseUri((cur) => cur || ns.data.namespaces[0] || '');
    }
  }

  function buildTemplate(): RdfSpreadsheetTemplate {
    const now = new Date().toISOString();
    const base = baseUri.trim();
    // Trim + UrlEncode so a cell value with spaces/reserved chars (e.g.
    // "Acme Corp") mints a syntactically valid IRI rather than breaking the
    // INSERT. UrlEncode is reversible, so identity/dedup stay stable.
    const idStrategy = (column: string) =>
      ({ FromColumn: { column, transforms: ['Trim' as const, 'UrlEncode' as const] } });

    const relationshipCols = columns.filter((c) => mapping(c).role === 'relationship');
    const propertyCols = columns.filter((c) => mapping(c).role === 'property');

    const pom = (c: string) => ({
      column_header: c,
      header_synonyms: [],
      predicate_uri: mapping(c).predicate.trim(),
      object_map: objectMapFor(mapping(c).datatype),
      required: false,
      role: 'Data' as const,
    });
    // Properties attach to the primary when attachTo is empty or points at a
    // column that is no longer a relationship (orphaned → fall back to primary).
    const propsFor = (target: string) =>
      propertyCols
        .filter((c) => {
          const at = mapping(c).attachTo;
          return target === ''
            ? at === '' || !relationshipCols.includes(at)
            : at === target;
        })
        .map(pom);

    const primary = {
      id: crypto.randomUUID(),
      subject_map: {
        class_uri: keyClass.trim(),
        uri_pattern: `${base}{id}`,
        id_strategy: idStrategy(keyColumn),
      },
      key_column: keyColumn,
      key_resolution:
        keyResolution === 'match_label'
          ? ({ MatchByLabel: { label_column: keyColumn } } as const)
          : ('Create' as const),
      predicate_object_maps: propsFor(''),
      ordinal: 0,
      link_predicate: '',
      also_as_property: null,
      skos_attach: null,
    };

    const relGroups = relationshipCols.map((c, i) => {
      const m = mapping(c);
      const skos: RdfSkosAttachment | null =
        m.skosMode === 'top' && m.skosScheme.trim()
          ? { TopOfScheme: { scheme_uri: m.skosScheme.trim() } }
          : m.skosMode === 'child' && m.skosParent.trim()
            ? { ChildOf: { parent_uri: m.skosParent.trim() } }
            : null;
      const alsoPom =
        m.alsoAsProp && m.alsoAsPropPredicate.trim()
          ? {
              column_header: c,
              header_synonyms: [],
              predicate_uri: m.alsoAsPropPredicate.trim(),
              object_map: { Literal: { datatype: null, lang: null } } as RdfObjectMap,
              required: false,
              role: 'Data' as const,
            }
          : null;
      return {
        id: crypto.randomUUID(),
        subject_map: {
          class_uri: m.targetClass.trim(),
          uri_pattern: `${base}{id}`,
          id_strategy: idStrategy(c),
        },
        key_column: c,
        key_resolution:
          m.resolution === 'match_label'
            ? ({ MatchByLabel: { label_column: c } } as const)
            : ('Create' as const),
        predicate_object_maps: propsFor(c),
        ordinal: i + 1,
        link_predicate: m.linkPredicate.trim(),
        also_as_property: alsoPom,
        skos_attach: skos,
      };
    });

    return {
      id: crypto.randomUUID(),
      name: file?.name ?? 'import',
      subject_map: { class_uri: '', uri_pattern: '', id_strategy: 'GeneratedUuid' },
      predicate_object_maps: [],
      target_graph_uri: null,
      upsert_key: 'Uri',
      unmapped_column_strategy: 'Skip',
      total_predicate: null,
      created: now,
      updated: now,
      entity_groups: [primary, ...relGroups],
      column_assignments: null,
      base_instance_uri: base,
    };
  }

  const relationshipColumns = columns.filter((c) => mapping(c).role === 'relationship');

  // Placeholder hint rooted in the data's own namespace, never "example.org".
  const nsHint = baseUri.trim() || namespaceOptions[0] || 'https://your.org/';
  const ph = (local: string) => `${nsHint}${local}`;

  const mapValid =
    keyColumn !== '' &&
    keyClass.trim() !== '' &&
    baseUri.trim() !== '' &&
    columns
      .filter((c) => mapping(c).role === 'property')
      .every((c) => mapping(c).predicate.trim() !== '') &&
    columns
      .filter((c) => mapping(c).role === 'relationship')
      .every((c) => {
        const m = mapping(c);
        return (
          m.targetClass.trim() !== '' &&
          m.linkPredicate.trim() !== '' &&
          (!m.alsoAsProp || m.alsoAsPropPredicate.trim() !== '') &&
          (m.skosMode !== 'top' || m.skosScheme.trim() !== '') &&
          (m.skosMode !== 'child' || m.skosParent.trim() !== '')
        );
      });

  async function runPreview() {
    setBusy(true);
    setError(null);
    const [pv, gl] = await Promise.all([
      rdfPreviewImport({ template: buildTemplate(), columns, rows }, token),
      listGraphs(token),
    ]);
    setBusy(false);
    if (!pv.ok) {
      setError(pv.message);
      return;
    }
    setPreview(pv.data);
    setChoices({});
    if (gl.ok) setGraphs(gl.data.graphs);
    setStep('preview');
  }

  /** The graph the commit actually writes to — the same expression the commit
   *  body uses, so the confirmation names the real target. */
  function effectiveGraph(): string {
    return targetGraph === '__new__' ? newGraph.trim() : targetGraph;
  }

  async function runCommit() {
    setConfirmReplace(false);
    setBusy(true);
    setError(null);
    const graph = effectiveGraph();
    // Echo the preview report back with the user's ambiguity choices applied.
    let report: RdfReconciliationReport | undefined;
    if (preview && preview.report.groups.length > 0) {
      report = {
        groups: preview.report.groups.map((g) => ({
          ...g,
          ambiguous: g.ambiguous.map((a) => ({
            ...a,
            user_choice: choices[a.tentative_uri] ?? a.user_choice,
          })),
        })),
      };
    }
    const body: {
      template: RdfSpreadsheetTemplate;
      columns: string[];
      rows: string[][];
      graph: string;
      mode: LoadMode;
      report?: RdfReconciliationReport;
    } = { template: buildTemplate(), columns, rows, graph, mode: writeMode };
    if (report) body.report = report;
    const res = await rdfCommitImport(body, token);
    setBusy(false);
    if (!res.ok) {
      setError(res.message);
      return;
    }
    setCommitResult(res.data);
    invalidateAfterDataWrite(queryClient);
    setStep('done');
  }

  function reset() {
    setStep('load');
    setFile(null);
    setColumns([]);
    setRows([]);
    setSheetNames([]);
    setSelectedSheet(null);
    setKeyColumn('');
    setKeyClass('');
    setMappings({});
    setPreview(null);
    setChoices({});
    setCommitResult(null);
    setError(null);
  }

  const ambiguousCount = preview?.report.groups.reduce((n, g) => n + g.ambiguous.length, 0) ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex gap-1 border-b border-border">
        <TabButton active={step === 'load'} done={columns.length > 0}>
          1 · Load
        </TabButton>
        <TabButton active={step === 'map'} done={step === 'preview' || step === 'done'}>
          2 · Map
        </TabButton>
        <TabButton active={step === 'preview'} done={step === 'done'}>
          3 · Preview
        </TabButton>
        <TabButton active={step === 'done'} done={false}>
          4 · Done
        </TabButton>
      </div>

      {error && (
        <div className={messageClass('warning')}>
          {error}
        </div>
      )}

      {/* ---- Step: Load ---- */}
      {step === 'load' && (
        <div className="space-y-4">
          <label className="block">
            <span className="text-sm font-medium">CSV or Excel file</span>
            <input
              type="file"
              accept=".csv,.tsv,.xlsx,.xls,.ods"
              disabled={busy}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                if (maxLoadBytes !== null && f.size > maxLoadBytes) {
                  setError(`File too large (this instance's maximum is ${formatBytes(maxLoadBytes)}).`);
                  return;
                }
                void analyze(f);
              }}
              className="mt-1 block w-full text-sm file:mr-4 file:rounded-md file:border-0 file:bg-secondary file:px-4 file:py-2 file:text-sm file:font-medium"
            />
          </label>

          {sheetNames.length > 1 && (
            <label className="block">
              <span className="text-sm font-medium">Sheet</span>
              <select
                className="mt-1 block rounded-md border border-border px-2 py-1 text-sm"
                value={selectedSheet ?? ''}
                disabled={busy}
                onChange={(e) => file && void analyze(file, e.target.value)}
              >
                {sheetNames.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className="block max-w-lg">
            <span className="text-sm font-medium">Base IRI for new entities</span>
            <input
              type="text"
              list="rdf-namespace-options"
              value={baseUri}
              onChange={(e) => setBaseUri(e.target.value)}
              placeholder={namespaceOptions[0] ?? 'https://your.org/id/'}
              className="mt-1 block w-full rounded-md border border-border px-2 py-1 text-sm"
            />
            <datalist id="rdf-namespace-options">
              {namespaceOptions.map((ns) => (
                <option key={ns} value={ns} />
              ))}
            </datalist>
            <span className="mt-1 block text-xs text-tres-text-secondary">
              {namespaceOptions.length > 0
                ? 'Suggested from your data graph. Subject IRIs are this base + the key column value.'
                : 'Subject IRIs are this base + the key column value.'}
            </span>
          </label>

          {columns.length > 0 && (
            <>
              <DataPreview columns={columns} rows={rows} />
              <button
                type="button"
                disabled={busy}
                onClick={() => setStep('map')}
                className={buttonVariants({ variant: 'primary', size: 'lg' })}
              >
                Next: Map columns
              </button>
            </>
          )}
        </div>
      )}

      {/* ---- Step: Map ---- */}
      {step === 'map' && (
        <div className="space-y-5">
          <div className="rounded-md border border-border p-3">
            <div className="text-sm font-semibold">Key column (the entity each row is about)</div>
            <div className="mt-2 grid gap-3 sm:grid-cols-3">
              <label className="block">
                <span className="text-xs text-tres-text-secondary">Column</span>
                <select
                  className="mt-1 block w-full rounded-md border border-border px-2 py-1 text-sm"
                  value={keyColumn}
                  onChange={(e) => setKeyColumn(e.target.value)}
                >
                  <option value="">— select —</option>
                  {columns.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-xs text-tres-text-secondary">Entity class IRI</span>
                <input
                  type="text"
                  value={keyClass}
                  onChange={(e) => setKeyClass(e.target.value)}
                  placeholder={ph('Person')}
                  className="mt-1 block w-full rounded-md border border-border px-2 py-1 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-xs text-tres-text-secondary">Identity</span>
                <select
                  className="mt-1 block w-full rounded-md border border-border px-2 py-1 text-sm"
                  value={keyResolution}
                  onChange={(e) => setKeyResolution(e.target.value as Resolution)}
                >
                  <option value="create">Always create new</option>
                  <option value="match_label">Match existing by label, else create</option>
                </select>
              </label>
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-sm font-semibold">Other columns</div>
            {columns
              .filter((c) => c !== keyColumn)
              .map((c) => {
                const m = mapping(c);
                return (
                  <div key={c} className="rounded-md border border-border p-3">
                    <div className="flex items-center gap-3">
                      <span className="min-w-32 text-sm font-medium">{c}</span>
                      <select
                        className="rounded-md border border-border px-2 py-1 text-sm"
                        value={m.role}
                        onChange={(e) => patchMapping(c, { role: e.target.value as Role })}
                      >
                        <option value="skip">Skip</option>
                        <option value="property">Property (literal)</option>
                        <option value="relationship">Relationship (link to entity)</option>
                      </select>
                    </div>

                    {m.role === 'property' && (
                      <div className="mt-2 grid gap-3 sm:grid-cols-3">
                        <label className="block">
                          <span className="text-xs text-tres-text-secondary">Predicate IRI</span>
                          <input
                            type="text"
                            value={m.predicate}
                            onChange={(e) => patchMapping(c, { predicate: e.target.value })}
                            placeholder={ph('name')}
                            className="mt-1 block w-full rounded-md border border-border px-2 py-1 text-sm"
                          />
                        </label>
                        <label className="block">
                          <span className="text-xs text-tres-text-secondary">Datatype</span>
                          <select
                            className="mt-1 block w-full rounded-md border border-border px-2 py-1 text-sm"
                            value={m.datatype}
                            onChange={(e) =>
                              patchMapping(c, { datatype: e.target.value as Datatype })
                            }
                          >
                            <option value="string">Text</option>
                            <option value="number">Number</option>
                            <option value="date">Date</option>
                            <option value="boolean">Boolean</option>
                          </select>
                        </label>
                        <label className="block">
                          <span className="text-xs text-tres-text-secondary">Attach to</span>
                          <select
                            className="mt-1 block w-full rounded-md border border-border px-2 py-1 text-sm"
                            value={m.attachTo}
                            onChange={(e) => patchMapping(c, { attachTo: e.target.value })}
                          >
                            <option value="">Primary entity</option>
                            {relationshipColumns.map((rc) => (
                              <option key={rc} value={rc}>
                                {rc} (related entity)
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                    )}

                    {m.role === 'relationship' && (
                      <div className="mt-2 space-y-3">
                        <div className="grid gap-3 sm:grid-cols-3">
                          <label className="block">
                            <span className="text-xs text-tres-text-secondary">Link predicate IRI</span>
                            <input
                              type="text"
                              value={m.linkPredicate}
                              onChange={(e) => patchMapping(c, { linkPredicate: e.target.value })}
                              placeholder={ph('worksFor')}
                              className="mt-1 block w-full rounded-md border border-border px-2 py-1 text-sm"
                            />
                          </label>
                          <label className="block">
                            <span className="text-xs text-tres-text-secondary">Target class IRI</span>
                            <input
                              type="text"
                              value={m.targetClass}
                              onChange={(e) => patchMapping(c, { targetClass: e.target.value })}
                              placeholder={ph('Org')}
                              className="mt-1 block w-full rounded-md border border-border px-2 py-1 text-sm"
                            />
                          </label>
                          <label className="block">
                            <span className="text-xs text-tres-text-secondary">Identity</span>
                            <select
                              className="mt-1 block w-full rounded-md border border-border px-2 py-1 text-sm"
                              value={m.resolution}
                              onChange={(e) =>
                                patchMapping(c, { resolution: e.target.value as Resolution })
                              }
                            >
                              <option value="match_label">Match by label, else create</option>
                              <option value="create">Always create new</option>
                            </select>
                          </label>
                        </div>

                        <details className="rounded-md border border-border px-3 py-2">
                          <summary className="cursor-pointer text-xs font-medium text-tres-text-secondary">
                            Advanced — also-as-property, SKOS placement
                          </summary>
                          <div className="mt-3 space-y-3">
                            <div className="grid gap-3 sm:grid-cols-2">
                              <label className="flex items-center gap-2 text-xs">
                                <input
                                  type="checkbox"
                                  checked={m.alsoAsProp}
                                  onChange={(e) => patchMapping(c, { alsoAsProp: e.target.checked })}
                                />
                                Also store “{c}” as a literal on the related entity
                              </label>
                              {m.alsoAsProp && (
                                <label className="block">
                                  <span className="text-xs text-tres-text-secondary">
                                    …with predicate IRI
                                  </span>
                                  <input
                                    type="text"
                                    value={m.alsoAsPropPredicate}
                                    onChange={(e) =>
                                      patchMapping(c, { alsoAsPropPredicate: e.target.value })
                                    }
                                    placeholder={ph('name')}
                                    className="mt-1 block w-full rounded-md border border-border px-2 py-1 text-sm"
                                  />
                                </label>
                              )}
                            </div>

                            <div className="grid gap-3 sm:grid-cols-2">
                              <label className="block">
                                <span className="text-xs text-tres-text-secondary">
                                  SKOS placement (for new concept targets)
                                </span>
                                <select
                                  className="mt-1 block w-full rounded-md border border-border px-2 py-1 text-sm"
                                  value={m.skosMode}
                                  onChange={(e) =>
                                    patchMapping(c, { skosMode: e.target.value as SkosMode })
                                  }
                                >
                                  <option value="none">None</option>
                                  <option value="top">Top concept of scheme</option>
                                  <option value="child">Narrower than a parent concept</option>
                                </select>
                              </label>
                              {m.skosMode === 'top' && (
                                <label className="block">
                                  <span className="text-xs text-tres-text-secondary">Scheme IRI</span>
                                  <input
                                    type="text"
                                    value={m.skosScheme}
                                    onChange={(e) => patchMapping(c, { skosScheme: e.target.value })}
                                    placeholder={ph('scheme')}
                                    className="mt-1 block w-full rounded-md border border-border px-2 py-1 text-sm"
                                  />
                                </label>
                              )}
                              {m.skosMode === 'child' && (
                                <label className="block">
                                  <span className="text-xs text-tres-text-secondary">
                                    Parent concept IRI
                                  </span>
                                  <input
                                    type="text"
                                    value={m.skosParent}
                                    onChange={(e) => patchMapping(c, { skosParent: e.target.value })}
                                    placeholder={ph('ParentConcept')}
                                    className="mt-1 block w-full rounded-md border border-border px-2 py-1 text-sm"
                                  />
                                </label>
                              )}
                            </div>
                          </div>
                        </details>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setStep('load')}
              className="rounded-md border border-border px-4 py-2 text-sm font-medium"
            >
              Back
            </button>
            <button
              type="button"
              disabled={busy || !mapValid}
              onClick={() => void runPreview()}
              className={buttonVariants({ variant: 'primary', size: 'lg' })}
            >
              {busy ? 'Working…' : 'Next: Preview'}
            </button>
          </div>
        </div>
      )}

      {/* ---- Step: Preview ---- */}
      {step === 'preview' && preview && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="New" value={preview.new_instances} />
            <Stat label="Matched/updated" value={preview.updated_instances} />
            <Stat label="Skipped" value={preview.skipped_rows} />
            <Stat label="Triples" value={preview.total_triples} />
          </div>

          {preview.warnings.length > 0 && (
            <ul className={cn(messageClass('platform', 'compact', 'warning'), 'list-disc px-6')}>
              {preview.warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          )}

          {preview.validation_errors.length > 0 && (
            <div className={messageClass('warning', 'compact')}>
              <div className="font-semibold">
                {preview.validation_errors.length} row issue(s) — those rows are skipped:
              </div>
              <ul className="mt-1 list-disc pl-4">
                {preview.validation_errors.slice(0, 10).map((e, i) => (
                  <li key={i}>
                    Row {e.row} · {e.column}: {e.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {ambiguousCount > 0 && (
            <div className="rounded-md border border-border p-3">
              <div className="text-sm font-semibold">
                Resolve {ambiguousCount} ambiguous match(es)
              </div>
              {preview.report.groups.flatMap((g) =>
                g.ambiguous.map((a) => (
                  <div key={a.tentative_uri} className="mt-2 border-t border-border pt-2">
                    <div className="text-xs font-medium">
                      “{a.key_value}” ({g.class_display})
                    </div>
                    <div className="mt-1 space-y-1">
                      {a.candidates.map((cand) => (
                        <label key={cand.uri} className="flex items-center gap-2 text-xs">
                          <input
                            type="radio"
                            name={a.tentative_uri}
                            checked={
                              typeof choices[a.tentative_uri] === 'object' &&
                              (choices[a.tentative_uri] as { UseExisting: string }).UseExisting ===
                                cand.uri
                            }
                            onChange={() =>
                              setChoices((c) => ({
                                ...c,
                                [a.tentative_uri]: { UseExisting: cand.uri },
                              }))
                            }
                          />
                          <span>
                            {cand.label || cand.uri}{' '}
                            <span className="text-tres-text-secondary">{cand.uri}</span>
                          </span>
                        </label>
                      ))}
                      <label className="flex items-center gap-2 text-xs">
                        <input
                          type="radio"
                          name={a.tentative_uri}
                          checked={choices[a.tentative_uri] === 'CreateNew'}
                          onChange={() =>
                            setChoices((c) => ({ ...c, [a.tentative_uri]: 'CreateNew' }))
                          }
                        />
                        <span>Create a new entity</span>
                      </label>
                    </div>
                  </div>
                )),
              )}
            </div>
          )}

          <div className="rounded-md border border-border p-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="text-xs text-tres-text-secondary">Target graph</span>
                <select
                  className="mt-1 block w-full rounded-md border border-border px-2 py-1 text-sm"
                  value={targetGraph}
                  onChange={(e) => setTargetGraph(e.target.value)}
                >
                  {!graphs.some((g) => g.bare === DEFAULT_GRAPH) && (
                    <option value={DEFAULT_GRAPH}>{DEFAULT_GRAPH} (data)</option>
                  )}
                  {graphs.map((g) => (
                    <option key={g.bare} value={g.bare}>
                      {g.bare}
                      {g.kind ? ` (${g.kind})` : ''}
                    </option>
                  ))}
                  <option value="__new__">＋ New graph…</option>
                </select>
              </label>
              {targetGraph === '__new__' && (
                <label className="block">
                  <span className="text-xs text-tres-text-secondary">New graph IRI</span>
                  <input
                    type="text"
                    value={newGraph}
                    onChange={(e) => setNewGraph(e.target.value)}
                    placeholder="urn:tres:data"
                    className="mt-1 block w-full rounded-md border border-border px-2 py-1 text-sm"
                  />
                </label>
              )}
              <label className="block">
                <span className="text-xs text-tres-text-secondary">Write mode</span>
                <select
                  className="mt-1 block w-full rounded-md border border-border px-2 py-1 text-sm"
                  value={writeMode}
                  onChange={(e) => setWriteMode(e.target.value as LoadMode)}
                >
                  <option value="append">Append</option>
                  <option value="replace">Replace graph</option>
                </select>
              </label>
            </div>
          </div>

          {preview.sample_triples.length > 0 && (
            <details className="rounded-md border border-border p-3">
              <summary className="cursor-pointer text-sm font-medium">
                Sample triples{preview.truncated ? ` (first ${preview.sample_triples.length})` : ''}
              </summary>
              <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap text-xs text-tres-text-secondary">
                {preview.sample_triples.join('\n')}
              </pre>
            </details>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setStep('map')}
              className="rounded-md border border-border px-4 py-2 text-sm font-medium"
            >
              Back
            </button>
            <button
              type="button"
              disabled={busy || preview.total_triples === 0}
              onClick={() =>
                writeMode === 'replace' ? setConfirmReplace(true) : void runCommit()
              }
              className={buttonVariants({ variant: 'primary', size: 'lg' })}
            >
              {busy ? 'Importing…' : 'Import'}
            </button>
          </div>
        </div>
      )}

      {confirmReplace && (
        <ConfirmGraphReplaceModal
          graph={effectiveGraph()}
          what="this spreadsheet"
          onCancel={() => setConfirmReplace(false)}
          onConfirm={() => void runCommit()}
        />
      )}

      {/* ---- Step: Done ---- */}
      {step === 'done' && commitResult && (
        <div className="space-y-4">
          <div className={messageClass('outcome')}>
            Imported {commitResult.triples_written} triples into{' '}
            <code>{commitResult.graph}</code> — {commitResult.rows_imported} new,{' '}
            {commitResult.rows_updated} updated, {commitResult.rows_skipped} skipped.
          </div>
          {commitResult.errors.length > 0 && (
            <div className={messageClass('warning', 'compact')}>
              <div className="font-semibold">{commitResult.errors.length} error(s):</div>
              <ul className="mt-1 list-disc pl-4">
                {commitResult.errors.slice(0, 10).map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          )}
          <button
            type="button"
            onClick={reset}
            className={buttonVariants({ variant: 'primary', size: 'lg' })}
          >
            Import another file
          </button>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="text-xs uppercase tracking-wide text-tres-text-secondary">{label}</div>
      <div className="mt-1 text-xl font-semibold text-tres-text-primary">
        {value.toLocaleString()}
      </div>
    </div>
  );
}

function DataPreview({ columns, rows }: { columns: string[]; rows: string[][] }) {
  return (
    <div className="overflow-auto rounded-md border border-border">
      <table className="w-full text-xs">
        <thead className="bg-muted">
          <tr>
            {columns.map((c) => (
              <th key={c} className="whitespace-nowrap px-2 py-1 text-left font-semibold">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 5).map((r, i) => (
            <tr key={i} className="border-t border-border">
              {columns.map((_, j) => (
                <td key={j} className="whitespace-nowrap px-2 py-1 text-tres-text-secondary">
                  {r[j] ?? ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
